import { useEffect, useMemo } from 'react';
import type { NodeDimensionChange } from '../../core';
import { useStoreApi } from '../../store';
import type { HandleBoundsUpdate } from '../../store';
import { measureHandles } from '../../utils';

/**
 * One ResizeObserver for all nodes of a canvas. Reports layout sizes (`offsetWidth`/`offsetHeight`, which
 * ignore the zoom transform) as `dimensions` changes and re-measures the node's handles at the same time (v11).
 * `null` where ResizeObserver is missing (jsdom).
 */
export const useNodeResizeObserver = (): ResizeObserver | null => {
  const store = useStoreApi();
  const observer = useMemo(() => {
    if (typeof ResizeObserver === 'undefined') return null;
    return new ResizeObserver((entries) => {
      const { viewport, updateNodeDimensions, setHandleBounds } = store.getState();
      const changes: NodeDimensionChange[] = [];
      const handleUpdates: HandleBoundsUpdate[] = [];
      entries.forEach(({ target }) => {
        const element = target as HTMLElement;
        const id = element.dataset.id;
        if (!id) return;
        changes.push({ id, type: 'dimensions', dimensions: { width: element.offsetWidth, height: element.offsetHeight } });
        handleUpdates.push({ id, handleBounds: measureHandles(element, viewport.zoom) });
      });
      setHandleBounds(handleUpdates);
      updateNodeDimensions(changes);
    });
  }, [store]);
  useEffect(() => () => observer?.disconnect(), [observer]);
  return observer;
};
